import { z } from 'zod';

import { MOBILE_PATTERN, normalizeMobile } from '@/utils/phone';

/**
 * Zod building blocks for the backend's data formats (see the backend README, "Data formats").
 * Feature schemas compose these so every screen parses money and dates the same way.
 */

/** Money as the API sends it: a 2-decimal string such as `"1500.00"`. Never a float. */
export const moneySchema = z.string().regex(/^\d+\.\d{2}$/, 'Invalid amount');
export type Money = z.infer<typeof moneySchema>;

/** A calendar date, `"YYYY-MM-DD"`. Keep it a string — `new Date()` would shift the day. */
export const dateOnlySchema = z.iso.date();
export type DateOnly = z.infer<typeof dateOnlySchema>;

/** A timestamp, ISO 8601 in UTC. */
export const timestampSchema = z.iso.datetime();

export const roleSummarySchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
});
export type RoleSummary = z.infer<typeof roleSummarySchema>;

export const userStatusSchema = z.enum(['active', 'disabled']);
export type UserStatus = z.infer<typeof userStatusSchema>;

/** A login account (staff user or member login) as `/users` and `/members/:id/account` return it. */
export const userAccountSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  mobile: z.string().nullable(),
  role: roleSummarySchema,
  status: userStatusSchema,
  memberId: z.number().nullable(),
  mustChangePassword: z.boolean(),
  lastLoginAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type UserAccount = z.infer<typeof userAccountSchema>;

/**
 * Just enough of a staff account to name them elsewhere — on a member row, an enquiry, a bill.
 * `name` is optional on an account, so use `staffRefName` rather than reading it directly.
 */
export const staffRefSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  mobile: z.string().nullable(),
});
export type StaffRef = z.infer<typeof staffRefSchema>;

/** Returned when an account is created or its password reset: the one-time password, once. */
export const accountWithPasswordSchema = z.object({
  account: userAccountSchema,
  temporaryPassword: z.string().nullable(),
});
export type AccountWithPassword = z.infer<typeof accountWithPasswordSchema>;

// --- Form-side rules (mirror the backend's request validation) ---------------------------

/**
 * Same rule as the API: an Indian mobile, 10 digits starting 6–9. Spaces, dashes and a `+91` / `0`
 * prefix are accepted and stripped, so what is sent is always `9922159684`.
 */
export const mobileInputSchema = z
  .string()
  .transform(normalizeMobile)
  .pipe(
    z
      .string()
      .regex(MOBILE_PATTERN, 'Enter a 10-digit mobile number (it starts with 6, 7, 8 or 9)'),
  );

export const emailInputSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email('Enter a valid email'));

/** Amount typed into a form: up to 8 digits and 2 decimals. */
export const moneyInputSchema = z
  .string()
  .trim()
  .regex(/^\d{1,8}(\.\d{1,2})?$/, 'Enter an amount like 1500 or 1500.50');

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

export const newPasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(PASSWORD_MAX_LENGTH, `Use at most ${PASSWORD_MAX_LENGTH} characters`);

/** An optional text input: blank becomes `null`, so the API clears the field. */
export function optionalText(max: number) {
  return z
    .string()
    .trim()
    .max(max, `Use at most ${max} characters`)
    .transform((value) => (value === '' ? null : value));
}

/**
 * An optional id chosen from a `<select>`: the value is a string there, blank when nothing is
 * picked, and the API wants a number or `null`.
 */
export const optionalId = z.union([
  z.literal('').transform(() => null),
  z.string().regex(/^\d+$/).transform(Number),
]);

/** An optional input run through `schema` only when something was typed; blank → `null`. */
export function optionalInput<T extends z.ZodType<unknown, string>>(schema: T) {
  return z
    .string()
    .trim()
    .pipe(z.union([z.literal('').transform(() => null), schema]));
}
